import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import {
  IAPICreateBusinessModel,
  IAPIPrimaryContactsModel,
} from "../interfaces";
import { CountryWithStateModel } from "./CountryModels"; 

export class CreateBusinessInputModel {
  businessName: string;
  primaryContactDetails: PrimaryContactDetails;
  errors: InputErrorModel<CreateBusinessInputModel>[] = [];
  businessNameError: string;
  constructor(data?: Partial<CreateBusinessInputModel>) {
    this.businessName = data?.businessName || "";
    this.primaryContactDetails = new PrimaryContactDetails(
      data?.primaryContactDetails
    );
    this.businessNameError = data?.businessNameError || "";
    this.errors =
      (data &&
        data?.errors?.map((e) => {
          return new InputErrorModel<CreateBusinessInputModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CreateBusinessInputModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  getErrorMessage(field: keyof CreateBusinessInputModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CreateBusinessInputModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.businessName,
      "businessName",
      newErrors,
      undefined,
      "Please enter business name"
    );
    const isValidContact = this.primaryContactDetails.validate(isAutoRun);

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;

    return this.errors.length === 0 && isValidContact;
  }

  createBusinessAPIModel(): IAPICreateBusinessModel {
    return {
      accountId: AuthService.authData.accountId,
      name: this.businessName,
      ...this.primaryContactDetails.createAPIModel(),
      mindmeAccountDetails: AuthService.mindmeAccountDetails,
    } as IAPICreateBusinessModel;
  }
}

export class PrimaryContactDetails {
  emailAddress: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  countryDetails: CountryWithStateModel;
  errors: InputErrorModel<PrimaryContactDetails>[];

  constructor(data?: Partial<PrimaryContactDetails>) {
    this.emailAddress = data?.emailAddress || "";
    this.phoneNumber = data?.phoneNumber || "";
    this.firstName = data?.firstName || "";
    this.lastName = data?.lastName || "";
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.errors =
      (data &&
        data?.errors?.map((e) => {
          return new InputErrorModel<PrimaryContactDetails>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof PrimaryContactDetails): boolean {
    return this.errors.some((error) => error.field === key);
  }

  getErrorMessage(field: keyof PrimaryContactDetails): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error.message || "" : "";
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PrimaryContactDetails>[] = [];

    CoreInputModelUtil.validateEmail(
      "emailAddress",
      this.emailAddress,
      newErrors
    );
    CoreInputModelUtil.validatePhoneNumber(
      "phoneNumber",
      this.phoneNumber,
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.firstName,
      "firstName",
      newErrors,
      undefined,
      "Please enter first name"
    );
    CoreInputModelUtil.validateTextField(
      this.lastName,
      "lastName",
      newErrors,
      undefined,
      "Please enter last name"
    );
    CoreInputModelUtil.validateTextField(
      this.countryDetails.abbreviationName,
      "countryDetails",
      newErrors,
      undefined,
      "Please select country"
    );

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIPrimaryContactsModel {
    return {
      email: this.emailAddress,
      phoneNumber: this.phoneNumber,
      phoneExtension: this.countryDetails.code.toString(),
      firstName: this.firstName,
      lastName: this.lastName,
      country: this.countryDetails.abbreviationName,
    };
  }
}
